/**
 * wine-box.js — Build Wine Box (wineengine / thewineengine only).
 *
 * Parity with old main.js Build Wine Box flow:
 *   membership cards → Build Now (3/6/12) → wine listing + drawer →
 *   Add to Basket / qty → Continue → Your Cart (monthly | one-off) → checkout.
 */

import {
  on,
  emit,
  getState,
  setState,
  addMessage,
} from "../../../core/state.js";
import * as api from "../../../core/api.js";
import { getTenantId } from "../../../core/configAccessors.js";
import { error } from "../../../core/env.js";
import { showBasicRecommendation, showEmpty } from "../../../features/optional/cart.js";

export const BUILD_BOX_MESSAGE = "Build a wine box";
export const AVAILABLE_WINES_MESSAGE = "Show me all available wines";

export const BUILD_BOX_TIERS = {
  3: {
    bottles: 3,
    discount: 5,
    label: "3 bottles",
    saveLabel: "Save 5%",
    discountCode: "box-discount-3QVN3B",
  },
  6: {
    bottles: 6,
    discount: 10,
    label: "6 bottles",
    saveLabel: "Save 10%",
    discountCode: "box-discount-TZQSEO",
  },
  12: {
    bottles: 12,
    discount: 15,
    label: "+12 bottles",
    saveLabel: "Save 15%",
    discountCode: "box-discount-Y8IIIY",
  },
};

const PRODUCT_MAX_QTY = 12;

let started = false;
let shadowRoot = null;

const defaultBuildBox = () => ({
  active: false,
  tier: 3,
  frequency: "monthly",
  summaryFrequency: "oneoff",
  sellingPlanId: null,
  drawerVisible: false,
  // When true, cart.js routes View Basket / header cart to build-box UI.
  interceptCart: true,
});

function bb() {
  return { ...defaultBuildBox(), ...(getState().buildBox || {}) };
}

function setBuildBox(patch) {
  setState({ buildBox: { ...bb(), ...patch } });
  emit("ws-build-box-changed", bb());
}

export function isBuildBoxMode() {
  return !!bb().active;
}

export function getBuildBoxState() {
  return bb();
}

export function enableBuildBoxMode(tier = 3, frequency = "monthly") {
  setBuildBox({
    active: true,
    tier: [3, 6, 12].includes(tier) ? tier : 3,
    frequency: frequency === "oneoff" ? "oneoff" : "monthly",
    drawerVisible: false,
  });
}

/** Remove drawer UI only (old unmountWineBoxDrawer). Keeps build-box mode. */
export function unmountWineBoxDrawer(shadow = shadowRoot) {
  const root = shadow || shadowRoot;
  hideBuildBoxToast(root);
  const panel = root?.querySelector?.(".ws-panel");
  panel?.classList.remove("has-wine-box-drawer");
  if (bb().drawerVisible) {
    setBuildBox({ drawerVisible: false });
  }
}

/** Exit build-box mode and remove drawer (old disableBuildBoxMode). */
export function disableBuildBoxMode() {
  unmountWineBoxDrawer(shadowRoot);
  setBuildBox({
    active: false,
    sellingPlanId: null,
    drawerVisible: false,
  });
}

function isShopOrColorWinesMessage(value) {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (v === AVAILABLE_WINES_MESSAGE.toLowerCase()) return true;
  return [
    "show me all red wines",
    "show me all red wines.",
    "show me all white wines",
    "show me all white wines.",
    "show me all rose wines",
    "show me all rose wines.",
  ].includes(v);
}

export function parseTierFromTitle(title) {
  const match = String(title || "").match(/(\d+)\s*Bottle/i);
  if (!match) return 3;
  const n = parseInt(match[1], 10);
  return [3, 6, 12].includes(n) ? n : 3;
}

export function expandCartToBottles(cartItems) {
  const bottles = [];
  (cartItems || []).forEach((item) => {
    const qty = item.quantity || 1;
    const shortName = String(item.productTitle || "")
      .trim()
      .split(/\s+/)[0];
    for (let i = 0; i < qty; i += 1) {
      bottles.push({
        variantId: item.productVariantId,
        title: item.productTitle,
        shortName,
        image: item.image,
        price: item.price,
      });
    }
  });
  return bottles;
}

export function getDiscountPercent(bottleCount) {
  if (bottleCount < 3) return 0;
  if (bottleCount < 6) return 5;
  if (bottleCount < 12) return 10;
  return 15;
}

export function getWineBoxSlotCount(bottles, state = bb()) {
  if (state.frequency === "oneoff") {
    return Math.max(bottles.length + 1, 3);
  }
  const tierBottles = BUILD_BOX_TIERS[state.tier]?.bottles || 3;
  return Math.max(bottles.length, tierBottles);
}

export function computeBuildBoxPricing(cartItems, { isMonthly } = {}) {
  let originalTotal = 0;
  let currencySymbol = "£";
  for (const item of cartItems || []) {
    const qty = item.quantity ?? 1;
    const priceStr = item.price || "0.00";
    const numPrice = parseFloat(String(priceStr).replace(/[^\d.]/g, "")) || 0;
    originalTotal += numPrice * qty;
    const match = String(priceStr).match(/[^\d.\s]/);
    if (match) currencySymbol = match[0];
  }
  const bottles = expandCartToBottles(cartItems);
  let discountPercent = 0;
  if (isMonthly) discountPercent = getDiscountPercent(bottles.length);
  let finalTotal = originalTotal;
  let subtotalWas = "";
  if (discountPercent > 0) {
    finalTotal =
      Math.round(originalTotal * (1 - discountPercent / 100) * 100) / 100;
    subtotalWas = `${currencySymbol}${originalTotal.toFixed(2)}`;
  }
  return {
    bottles,
    bottleCount: bottles.length,
    discountPercent,
    subtotalWas,
    subtotalDisplay: `${currencySymbol}${finalTotal.toFixed(2)}`,
    currencySymbol,
    originalTotal,
  };
}

export function getBuildBoxShippingMessage(cartSummary) {
  if (cartSummary?.freeShippingUnlocked) {
    return `🎉 Yaay!! you've <span class="wine-box-shipping-unlocked">unlocked</span> free shipping.`;
  }
  const amount =
    cartSummary?.freeShippingRemaining ||
    cartSummary?.freeShippingThreshold ||
    "£75.00";
  return `🚚 Add worth <strong class="wine-box-shipping-amount">${amount}</strong> for free shipping`;
}

function evaluateBuildBoxTierChange(oldCount, newCount, currentTier) {
  let nextTier = 3;
  if (newCount >= 12) nextTier = 12;
  else if (newCount >= 6) nextTier = 6;
  else nextTier = 3;

  const oldDiscount = getDiscountPercent(oldCount);
  const newDiscount = getDiscountPercent(newCount);
  let toastType = null;
  if (newDiscount > oldDiscount) toastType = "upgrade";
  else if (newDiscount < oldDiscount) toastType = "downgrade";

  return { tier: nextTier || currentTier, toastType };
}

let toastTimer = null;

function toastHostEl(shadow) {
  return shadow?.querySelector?.("#wine-box-toast-host") || null;
}

export function hideBuildBoxToast(shadow) {
  if (toastTimer) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
  const host = toastHostEl(shadow);
  if (host) host.innerHTML = "";
}

export function showBuildBoxToast(shadow, type, selectedCount = 0) {
  const host = toastHostEl(shadow);
  if (!host) return;
  hideBuildBoxToast(shadow);

  const isUpgrade = type === "upgrade";
  const title = isUpgrade ? "Upgraded your subscription" : "Downgraded";
  const currentDiscount = getDiscountPercent(selectedCount);
  const message = `Your plan now includes ${currentDiscount}% off`;

  host.innerHTML = `
    <div class="wine-box-toast" role="status">
      <div class="wine-box-toast-icon ${isUpgrade ? "wine-box-toast-icon--upgrade" : "wine-box-toast-icon--downgrade"}">
        ${
          isUpgrade
            ? `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="28" height="28" rx="14" fill="#2A8040"/><path d="M11.4286 20V14.4615H8L14 8L20 14.4615H16.5714V20H11.4286Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/></svg>`
            : `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="28" height="28" rx="14" fill="#E6B405"/><path d="M16.5714 8L16.5714 13.5385L20 13.5385L14 20L8 13.5385L11.4286 13.5385L11.4286 8L16.5714 8Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/></svg>`
        }
      </div>
      <div class="wine-box-toast-body">
        <strong>${title}</strong>
        <p>${message}</p>
      </div>
      <button type="button" class="wine-box-toast-close" data-wine-box-toast-close aria-label="Close">
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 0.5L0.5 12.5M0.5 0.5L12.5 12.5" stroke="#0A0A0A" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  `;
  host.querySelector("[data-wine-box-toast-close]")?.addEventListener("click", () =>
    hideBuildBoxToast(shadow),
  );
  toastTimer = setTimeout(() => hideBuildBoxToast(shadow), 5000);
}

function applyBuildBoxTierChange(shadow, oldCount, newCount) {
  const state = bb();
  if (state.frequency === "oneoff") return;
  const { tier, toastType } = evaluateBuildBoxTierChange(
    oldCount,
    newCount,
    state.tier,
  );
  if (tier !== state.tier) setBuildBox({ tier });
  if (toastType) showBuildBoxToast(shadow, toastType, newCount);
}

async function getCartSnapshot() {
  const cartId = getState().cart?.id;
  if (!cartId) {
    return { items: getState().cart?.items || [], cart: getState().cart?.summary || {} };
  }
  try {
    const cart = await api.getCart(cartId);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const summary = cart?.cart || null;
    setState({
      cart: { ...getState().cart, id: cart?.cartId ?? cartId, items, summary },
    });
    return { items, cart: summary || {}, cartId: cart?.cartId ?? cartId };
  } catch (e) {
    error("[wine-box] getCart failed", e);
    return {
      items: getState().cart?.items || [],
      cart: getState().cart?.summary || {},
    };
  }
}

export async function refreshWineBoxDrawer(shadow, cartData) {
  const drawer = shadow?.querySelector?.("[data-wine-box-drawer]");
  if (!drawer || !isBuildBoxMode()) return;

  const cart =
    cartData?.items && Array.isArray(cartData.items)
      ? cartData
      : await getCartSnapshot();
  const cartItems = cart?.items || [];
  const cartSummary = cart?.cart || {};
  let state = bb();
  const bottles = expandCartToBottles(cartItems);

  if (state.frequency !== "oneoff") {
    let nextTier = state.tier;
    if (bottles.length >= 12 && state.tier < 12) nextTier = 12;
    else if (bottles.length >= 6 && state.tier < 6) nextTier = 6;
    if (nextTier !== state.tier) {
      setBuildBox({ tier: nextTier });
      state = bb();
    }
  }

  const pricing = computeBuildBoxPricing(cartItems, {
    isMonthly: state.frequency !== "oneoff",
  });
  const slotCount = getWineBoxSlotCount(bottles, state);
  const isOneoff = state.frequency === "oneoff";

  drawer.classList.toggle("is-oneoff", isOneoff);
  drawer.classList.add("is-visible");

  const countEl = drawer.querySelector("[data-wine-box-count]");
  const slotsEl = drawer.querySelector("[data-wine-box-slots]");
  const subtotalPricesEl = drawer.querySelector("[data-wine-box-subtotal-prices]");
  const shippingEl = drawer.querySelector("[data-wine-box-shipping]");
  const continueBtn = drawer.querySelector("[data-wine-box-continue]");

  if (countEl) countEl.textContent = String(pricing.bottleCount);
  if (slotsEl) {
    slotsEl.innerHTML = renderSlotsMarkup(bottles, slotCount);
    slotsEl.querySelectorAll("[data-wine-box-slot-remove]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = parseInt(btn.getAttribute("data-wine-box-slot-remove"), 10);
        removeBottleAtIndex(shadow, idx);
      });
    });
  }
  if (subtotalPricesEl) {
    if (isOneoff || pricing.discountPercent === 0) {
      subtotalPricesEl.innerHTML = `<span class="wine-box-subtotal-now">${pricing.subtotalDisplay}</span>`;
    } else {
      subtotalPricesEl.innerHTML = `
        <s class="build-box-cart-subtotal-was" style="color:#9b9b9b;font-size:14px;margin-right:6px;">${pricing.subtotalWas}</s>
        <strong class="wine-box-subtotal-now" style="font-size:16px;font-weight:700;color:#1d1d1d;">${pricing.subtotalDisplay}</strong>
      `;
    }
  }
  if (shippingEl) shippingEl.innerHTML = getBuildBoxShippingMessage(cartSummary);
  if (continueBtn) continueBtn.disabled = pricing.bottleCount < 1;

  drawer.querySelectorAll("[data-wine-box-tier]").forEach((btn) => {
    const tier = parseInt(btn.dataset.wineBoxTier, 10);
    const isActive = tier === state.tier;
    btn.classList.toggle("is-active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  });
  drawer.querySelectorAll("[data-wine-box-frequency]").forEach((btn) => {
    const frequency = btn.dataset.wineBoxFrequency;
    const isActive = frequency === state.frequency;
    btn.classList.toggle("is-active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  });
}

function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function renderSlotsMarkup(bottles, slotCount) {
  const slots = [];
  for (let i = 0; i < slotCount; i += 1) {
    const bottle = bottles[i];
    if (bottle) {
      slots.push(`
        <div class="wine-box-slot is-filled">
          <button type="button" class="wine-box-slot-remove" data-wine-box-slot-remove="${i}" aria-label="Remove ${esc(bottle.title)}">&times;</button>
          <span class="wine-box-slot-label">${esc(bottle.title)}</span>
        </div>
      `);
    } else {
      slots.push(`
        <div class="wine-box-slot is-empty" aria-hidden="true">Slot ${i + 1}</div>
      `);
    }
  }
  return slots.join("");
}

async function removeBottleAtIndex(shadow, bottleIndex) {
  const cart = await getCartSnapshot();
  const bottles = expandCartToBottles(cart?.items);
  const bottle = bottles[bottleIndex];
  if (!bottle) return;
  const oldCount = bottles.length;
  const variantQty = bottles.filter((b) => b.variantId === bottle.variantId).length;
  const cartId = getState().cart?.id;
  if (!cartId) return;

  emit("ws-cart-busy", { active: true });
  try {
    let result;
    if (variantQty <= 1) {
      result = await api.removeCartItem({
        cartId,
        variantIds: [bottle.variantId],
      });
    } else {
      const line = (getState().cart.items || []).find(
        (it) => String(it.productVariantId) === String(bottle.variantId),
      );
      result = await api.updateCartItem({
        cartId,
        lines: [
          {
            ...(line?.id ? { id: line.id } : {}),
            variantId: bottle.variantId,
            quantity: variantQty - 1,
          },
        ],
      });
    }
    const items = Array.isArray(result?.items) ? result.items : [];
    setState({
      cart: {
        ...getState().cart,
        id: result?.cartId ?? cartId,
        items,
        summary: result?.cart || null,
      },
    });
    const count = items.reduce((n, it) => n + (Number(it.quantity) || 0), 0);
    emit("ws-cart-count", { count });
    const newCount = expandCartToBottles(items).length;
    applyBuildBoxTierChange(shadow, oldCount, newCount);
    await refreshWineBoxDrawer(shadow, { items, cart: result?.cart || {} });
  } catch (e) {
    error("[wine-box] remove slot failed", e);
  } finally {
    emit("ws-cart-busy", { active: false });
  }
}

export function mountWineBoxDrawer(shadow) {
  if (!isBuildBoxMode()) return;
  setBuildBox({ drawerVisible: true });
  const panel = shadow?.querySelector?.(".ws-panel");
  panel?.classList.add("has-wine-box-drawer");
  refreshWineBoxDrawer(shadow);
}

export async function handleWineBoxContinue(shadow) {
  const cart = await getCartSnapshot();
  const bottleCount = expandCartToBottles(cart?.items).length;
  if (!bottleCount) return;

  const state = bb();
  // Old handleWineBoxContinue: unmount drawer, keep summary frequency for cart UI.
  unmountWineBoxDrawer(shadow);
  setBuildBox({
    summaryFrequency: state.frequency,
    drawerVisible: false,
    active: false,
  });

  addMessage({
    role: "bot",
    kind: "text",
    data: { text: "Here is the cart, please review it." },
  });
  addMessage({
    role: "bot",
    kind: "build-box-cart",
    isFirstResponse: false,
    data: {
      items: cart.items || [],
      cart: { items: cart.items || [], cart: cart.cart || {} },
      frequency: state.frequency,
      tier: state.tier,
    },
  });
}

export function getBuildBoxDiscountCode() {
  const state = bb();
  if (state.summaryFrequency !== "monthly") return "";
  const items = getState().cart?.items || [];
  const bottleCount = expandCartToBottles(items).length;
  const discountPercent = getDiscountPercent(bottleCount);
  if (discountPercent <= 0) return "";
  let tierKey = 3;
  if (discountPercent === 10) tierKey = 6;
  if (discountPercent === 15) tierKey = 12;
  return BUILD_BOX_TIERS[tierKey].discountCode;
}

export function showBuildBoxCartSummary(options = {}) {
  const items = getState().cart?.items || [];
  const summary = getState().cart?.summary || {};
  if (!items.length) {
    showEmpty();
    showBasicRecommendation();
    return;
  }
  const frequency = options.frequency || bb().summaryFrequency || "oneoff";
  setBuildBox({ summaryFrequency: frequency });

  // Old showCartsProducts: 3-dot loader → then cart summary + scroll to bottom.
  // Do NOT call get_question_recommendations here (cart-only UI).
  emit("ws-chat-typing", { active: true });
  setTimeout(() => {
    emit("ws-chat-typing", { active: false });
    addMessage({
      role: "bot",
      kind: "build-box-cart",
      // Avoid MessageList jumping to prior feature-card .ws-fr; pin to bottom.
      isFirstResponse: false,
      data: {
        items: getState().cart?.items || items,
        cart: {
          items: getState().cart?.items || items,
          cart: getState().cart?.summary || summary,
        },
        frequency,
        tier: options.tier || bb().tier,
      },
    });
  }, 500);
}

function clampQty(n) {
  return Math.min(PRODUCT_MAX_QTY, Math.max(1, n));
}

export function init({ shadow } = {}) {
  if (started) return;
  started = true;
  shadowRoot = shadow || null;

  setState({ buildBox: defaultBuildBox() });

  // Old option-button / cardClicked / sendTextMessage parity:
  // keep drawer only for Build Now + Shop/color wines; otherwise close it.
  on("ws-prompt-select", ({ value, buildBoxTier, buildBox, uniqueId } = {}) => {
    // Build Now / Edit box size → enter monthly build-box mode.
    if (buildBox || buildBoxTier != null) {
      enableBuildBoxMode(
        buildBoxTier != null ? parseInt(buildBoxTier, 10) : bb().tier || 3,
        "monthly",
      );
      return;
    }

    // Shop Our Wines / color filters → one-off build-box mode (drawer remounts on listing).
    if (isShopOrColorWinesMessage(value)) {
      enableBuildBoxMode(3, "oneoff");
      return;
    }

    // Learn more / card detail (uniqueId) → unmount drawer only (old cardClicked).
    if (uniqueId !== undefined && uniqueId !== null && uniqueId !== "") {
      unmountWineBoxDrawer(shadowRoot);
      return;
    }

    // Any other prompt — including __contact__ / quiz / offers / Build Wine Box chip.
    // Matches old option-button else → disableBuildBoxMode().
    disableBuildBoxMode();
  });

  on("ws-build-box-enable", ({ tier = 3, frequency = "monthly" } = {}) => {
    enableBuildBoxMode(tier, frequency);
  });

  // Typed chat input → always close (old sendTextMessage → disableBuildBoxMode).
  on("ws-chat-input-submit", () => {
    disableBuildBoxMode();
  });

  // Minimize / close panel → close drawer (old openAgent toggle).
  on("ws-toggle", ({ isOpen } = {}) => {
    if (!isOpen) disableBuildBoxMode();
  });

  // Header refresh → close drawer (old reloadChat).
  on("ws-messages-cleared", () => {
    disableBuildBoxMode();
  });

  on("ws-message-added", (msg) => {
    if (!isBuildBoxMode()) return;
    if (msg?.kind === "cards" && msg?.data?.cardType === "wine") {
      mountWineBoxDrawer(shadowRoot);
    }
  });

  // After cart mutations from WineCard — skip order summary (cart.js gate)
  // and refresh the drawer.
  on("ws-build-box-cart-mutated", async ({ cart, oldCount } = {}) => {
    if (!isBuildBoxMode()) return;
    const items = cart?.items || getState().cart?.items || [];
    const newCount = expandCartToBottles(items).length;
    if (typeof oldCount === "number") {
      applyBuildBoxTierChange(shadowRoot, oldCount, newCount);
    }
    await refreshWineBoxDrawer(shadowRoot, {
      items,
      cart: cart?.cart || cart?.summary || {},
    });
  });

  on("ws-cart-count", async () => {
    if (isBuildBoxMode() && bb().drawerVisible) {
      await refreshWineBoxDrawer(shadowRoot);
    }
  });

  // Header / View Basket → build-box cart UI (one-off), exit drawer mode.
  on("ws-build-box-open-cart", () => {
    disableBuildBoxMode();
    showBuildBoxCartSummary({ frequency: "oneoff" });
  });

  on("ws-build-box-continue", () => handleWineBoxContinue(shadowRoot));

  on("ws-build-box-set-tier", ({ tier } = {}) => {
    if (![3, 6, 12].includes(tier)) return;
    setBuildBox({ tier });
    refreshWineBoxDrawer(shadowRoot);
  });

  on("ws-build-box-set-frequency", ({ frequency } = {}) => {
    if (frequency !== "monthly" && frequency !== "oneoff") return;
    setBuildBox({ frequency });
    refreshWineBoxDrawer(shadowRoot);
  });

  on("ws-build-box-cart-frequency", ({ frequency } = {}) => {
    if (frequency !== "monthly" && frequency !== "oneoff") return;
    setBuildBox({ summaryFrequency: frequency });
    emit("ws-build-box-cart-rerender", { frequency });
  });

  on("ws-build-box-edit-size", () => {
    enableBuildBoxMode(bb().tier, "monthly");
    emit("ws-prompt-select", {
      label: AVAILABLE_WINES_MESSAGE,
      value: AVAILABLE_WINES_MESSAGE,
      buildBox: true,
      buildBoxTier: bb().tier,
    });
  });

  void getTenantId;
  void clampQty;
}
